/**
 * user-list-export.ts
 *
 * Reusable export confirmation modal for any user list page (org portal, school portal).
 *
 * Wires up a data-driven export flow:
 *   1. Export button click → reads count from a DOM element → populates modal body
 *   2. Confirm button click → builds export URL with current filter params → triggers download
 *   3. Disables export button for a cooldown period to prevent double-clicks
 *
 * Attach by adding `data-user-list-export-btn` to the trigger button.
 * Required sibling elements are discovered via data attributes on the list container:
 *   - `data-export-url`    — base export URL
 *   - `#totalUserCount`    — element whose textContent is the current row count
 *   - `#exportModal`       — the Bootstrap/Tabler confirmation modal
 *   - `#exportModalBody`   — modal body element (text is the template with `{0}` placeholder)
 *   - `#exportConfirmBtn`  — the confirm/download button inside the modal
 *   - `#searchTerm`        — (optional) search input whose value is forwarded as a query param
 *   - `#statusFilter`      — (optional) status select whose value is forwarded as a query param
 */

const COOLDOWN_MS = 3000;

/**
 * Initialises the user list export modal behaviour.
 * Safe to call on every page — no-op when the trigger button is absent.
 */
export function initUserListExport(): void {
  const exportBtn = document.querySelector<HTMLButtonElement>('[data-user-list-export-btn]');
  const confirmBtn = document.getElementById('exportConfirmBtn') as HTMLButtonElement | null;
  const modalBody = document.getElementById('exportModalBody');
  const listEl = document.querySelector<HTMLElement>('[data-export-url]');

  if (!exportBtn || !confirmBtn || !modalBody || !listEl) return;

  const exportBaseUrl = listEl.dataset['exportUrl'] ?? '';
  const bodyTemplate = modalBody.dataset['template'] ?? '';

  // Disable the export button while a list AJAX refresh is in flight
  listEl.addEventListener('list-refresh', ((e: CustomEvent<{ loading: boolean }>) => {
    exportBtn.disabled = e.detail.loading;
  }) as EventListener);

  // When the Export button is clicked (before modal opens), read the count from the DOM
  exportBtn.addEventListener('click', () => {
    const countEl = document.getElementById('totalUserCount');
    const count = countEl?.textContent?.trim() ?? '0';
    modalBody.textContent = bodyTemplate.replace('{0}', count);
  });

  // When the Confirm button in the modal is clicked, trigger the download
  confirmBtn.addEventListener('click', () => {
    const url = buildExportUrl(exportBaseUrl);
    window.location.href = url;

    // Close the modal via Bootstrap's data API
    const modalEl = document.getElementById('exportModal');
    if (modalEl) {
      const closeBtn = modalEl.querySelector<HTMLButtonElement>('[data-bs-dismiss="modal"]');
      closeBtn?.click();
    }

    // Cooldown: disable the Export button for 3 seconds
    exportBtn.disabled = true;
    setTimeout(() => {
      exportBtn.disabled = false;
    }, COOLDOWN_MS);
  });
}

/**
 * Builds the export URL with current filter query parameters.
 */
function buildExportUrl(baseUrl: string): string {
  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;

  const params = new URLSearchParams();
  const search = searchInput?.value.trim() ?? '';
  const status = statusSelect?.value ?? '';

  if (search) params.set('searchTerm', search);
  if (status) params.set('status', status);

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
